import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, errMsg, confirmDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { Search, Trash2 } from "lucide-react";

const STATUSES = ["Stokta", "Satıldı", "Rezerve", "Serviste", "Beklemede"];

export const VEHICLE_FIELDS = [
  { name: "plate", label: "Plaka", required: true },
  { name: "vehicle_type", label: "Araç Tipi", type: "select", options: ["Binek", "Ticari", "Kamyon", "İş Makinesi", "Diğer"] },
  { name: "brand", label: "Marka", required: true },
  { name: "model", label: "Model", required: true },
  { name: "year", label: "Yıl", type: "number", required: true },
  { name: "current_km", label: "Güncel Km", type: "number" },
  { name: "chassis_number", label: "Şasi No" },
  { name: "engine_number", label: "Motor No" },
  { name: "status", label: "Durum", type: "select", options: STATUSES.filter((s) => s !== "Satıldı") },
];

export default function Vehicles() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    api.get("/vehicles", { params: { q: q || undefined, status } }).then((r) => setData(r.data));
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "plate", label: "Plaka", className: "font-mono font-semibold" },
    { key: "brand", label: "Araç", render: (v) => <div><div className="font-medium">{v.brand} {v.model}</div><div className="text-xs text-muted-foreground">{v.year} · {v.vehicle_type} · {v.current_km?.toLocaleString("tr-TR")} km</div></div> },
    { key: "status", label: "Durum", render: (v) => <StatusBadge value={v.status} /> },
    { key: "purchase_price", label: "Alış", align: "right", render: (v) => money(v.purchase_price) },
    { key: "total_expenses", label: "Masraf", align: "right", render: (v) => money(v.total_expenses) },
    { key: "total_cost", label: "Toplam Maliyet", align: "right", render: (v) => <b>{money(v.total_cost)}</b> },
    { key: "sale_price", label: "Satış", align: "right", render: (v) => money(v.sale_price) },
    { key: "profit", label: "Kâr/Zarar", align: "right", render: (v) => v.profit == null ? "-" :
      <span className={v.profit >= 0 ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{money(v.profit)}</span> },
    { key: "trip_count", label: "Sefer", align: "right" },
    { key: "x", label: "", render: (v) => can("otomotiv") && <span onClick={(e) => e.stopPropagation()}><Button size="sm" variant="ghost" className="text-rose-600"
      onClick={() => del(v)} data-testid={`delete-vehicle-${v.id}`}><Trash2 className="w-4 h-4 mr-1" />Sil</Button></span> },
  ];
  const del = async (v) => {
    try { if (await confirmDelete(`/vehicles/${v.id}`)) { toast.success("Kayıt silindi"); load(); } } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <PageHeader title="Araçlar" subtitle={`${data.total} araç · maliyet ve kâr alış, masraf ve satış kayıtlarından hesaplanır`}>
        {can("otomotiv") && (
          <FormDialog title="Yeni Araç" triggerLabel="Yeni Araç" triggerTestId="add-vehicle-btn" url="/vehicles"
            successMsg="Araç kaydedildi" onDone={load} initial={{ status: "Stokta", vehicle_type: "Binek", year: 2024, current_km: 0, purchase_date: new Date().toISOString().slice(0, 10) }}
            fields={[...VEHICLE_FIELDS,
              { name: "purchase_price", label: "Alış Fiyatı (₺)", type: "number" },
              { name: "purchase_date", label: "Alış Tarihi", type: "date" },
              { name: "supplier_id", label: "Satıcı (Tedarikçi)", type: "entity", endpoint: "/suppliers" },
              { name: "notes", label: "Not", type: "textarea" }]} />
        )}
      </PageHeader>
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Plaka, marka, model, şasi..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicle-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="md:w-48" data-testid="vehicle-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tüm Durumlar</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DataTable columns={columns} rows={data.items} onRowClick={(v) => navigate(`/araclar/${v.id}`)} testIdPrefix="vehicle-row" />
      </Card>
    </div>
  );
}
